// Centralized API configuration and utilities
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://local-be.vnz98.online';
const N8N_WEBHOOK_URL = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://n8n.bridgex.global/webhook';
// API endpoints
export const API_ENDPOINTS = {
  LOGIN: '/auth/login',
  VERIFY_TOKEN: '/api/common/settings',
  ORDER_PURCHASES: '/api/order/purchases',
  ORDER_CREATE: '/api/order/new',
  ORDER_DETAIL: '/api/order/order-detail',
  ORDER_UPDATE: '/api/order/update',
  SKU_LOOKUP: '/api/lookup-sku',
  SKU_LOOKUP_MANY: '/api/lookup-sku/lookup-many-sku',
  DELETE_ORDER: '/api/order/delete',
  UPDATE_ORDER_PURCHASES: '/api/order/update',
  GET_ACTIVE_TAGS_MAP: '/api/tags/get-active-tags-map',
  PROCESS_IMPORT_PRODUCT: '/api/product-import/process',
  ALL_ACTIVE_TAGS: '/api/tags/get-active-tags-map',
  GET_ALL_TAG_IN_SYSTEM: `/api/get-all-tag-in-system`,
  NOTIFICATION_LOGS: '/notification-logs',
  // Transfer endpoints
  TRANSFER_ORDERS: '/api/transfer-orders',
  TRANSFER_ORDER_BY_ID: '/api/transfer-orders',
  TRANSFER_ORDER_BY_CODE: '/api/transfer-orders/code',
  TRANSFER_ORDER_CONFIRM: '/api/transfer-orders',
  TRANSFER_ORDER_COMPLETE: '/api/transfer-orders',
  TRANSFER_ORDER_CANCEL: '/api/transfer-orders',
  // User endpoints
  USERS: '/api/users',
  USERS_LIST: '/api/users/list',
  USER_BY_ID: '/api/users',
  USER_BY_USERNAME: '/api/users/username',
  USER_ASSIGN_ROLES: '/api/users',
  USER_REMOVE_ROLES: '/api/users',
  // RFID Scan Results endpoints
  RFID_SCAN_RESULTS: '/api/rfid-scan-results',
  RFID_SCAN_RESULT_BY_ID: '/api/rfid-scan-results',
  RFID_SCAN_RESULTS_BY_CLIENT: '/api/rfid-scan-results/client',
  RFID_SCAN_RESULTS_BY_DEVICE: '/api/rfid-scan-results/device',
  // MQTT Device endpoints
  MQTT_DEVICES: '/mqtt/devices',
  MQTT_DEVICE_BY_ID: '/mqtt/devices',
  MQTT_DEVICE_LOCATION: '/mqtt/devices',
  // Reports endpoints
  SKU_SERIALS_CHART: '/api/reports/dashboard/sku-serials-chart',
};

// Default headers
const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
});

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Add auth token to headers if available
const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = getDefaultHeaders();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

// Handle 401 unauthorized - logout and redirect to login
// Only removes auth-related localStorage items, preserves other data
export const handleUnauthorized = () => {
  // Only remove authentication-related localStorage items
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
  
  // Redirect to login page (using HashRouter format)
  // Use window.location to ensure full page reload and clear any React state
  window.location.href = '/#/login';
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  let url = `${API_BASE_URL}${endpoint}`;
  if (endpoint.startsWith('https://')) {
    url = endpoint;
  } else {
    url = `${API_BASE_URL}${endpoint}`;
  }
  const config = {
    headers: getAuthHeaders(),
    ...options,
  };

  try {
    const response = await fetch(url, config);

    // Handle 401 Unauthorized - automatically logout and redirect to login
    if (response.status === 401) {
      handleUnauthorized();
      // Throw error to prevent further processing
      throw new Error('Unauthorized: Session expired. Please log in again.');
    }

    // Handle non-JSON responses
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // Don't log 401 errors as they're handled by handleUnauthorized
    if (!error.message.includes('Unauthorized')) {
      console.error('API Request Error:', error);
    }
    throw error;
  }
};

// Login API call
export const loginAPI = async (username, password) => {
  try {
    const loginEndpoint = API_ENDPOINTS.LOGIN;
    const response = await apiRequest(loginEndpoint, {
      method: 'POST',
      headers: getDefaultHeaders(), // Don't include auth token for login
      body: JSON.stringify({
        username,
        password,
      }),
    });

    return response;
  } catch (error) {
    console.error('Login API Error:', error);
    throw error;
  }
};

// Verify token API call
export const verifyTokenAPI = async () => {
  try {
    const response = await apiRequest(API_ENDPOINTS.VERIFY_TOKEN, {
      method: 'GET',
    });

    // Return the full response including metaData, user, roles, permissions, and frontendPermissions
    return {
      success: true,
      metaData: response.metaData,
      user: response.user,
      roles: response.roles,
      permissions: response.permissions || [],
      frontendPermissions: response.frontendPermissions || [],
      rawResponse: response
    };
  } catch (error) {
    console.error('Token verification API Error:', error);
    throw error;
  }
};

// Generic GET request
export const get = async (endpoint) => {
  return apiRequest(endpoint, {
    method: 'GET',
  });
};

// Generic POST request
export const post = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Generic PUT request
export const put = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Generic PATCH request
export const patch = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

// Generic DELETE request
export const del = async (endpoint) => {
  return apiRequest(endpoint, {
    method: 'DELETE',
  });
};

// Order purchases API call
export const getOrderPurchases = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add query parameters if provided
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }
    if (params.page) {
      queryParams.append('page', params.page);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }

    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.ORDER_PURCHASES}?${queryParams.toString()}`
      : API_ENDPOINTS.ORDER_PURCHASES;

    const response = await apiRequest(endpoint, {
      method: 'GET',
    });

    return response;
  } catch (error) {
    console.error('Order purchases API Error:', error);
    throw error;
  }
};

// Order detail API call
export const getOrderDetail = async (orderId) => {
  try {
    const endpoint = `${API_ENDPOINTS.ORDER_DETAIL}?id=${orderId}`;

    const response = await apiRequest(endpoint, {
      method: 'GET',
    });

    return response;
  } catch (error) {
    console.error('Order detail API Error:', error);
    throw error;
  }
};

// Create order API call
export const createOrder = async (orderData) => {
  try {
    const response = await apiRequest(API_ENDPOINTS.ORDER_CREATE, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });

    return response;
  } catch (error) {
    console.error('Create order API Error:', error);
    throw error;
  }
};

// Update order API call
export const updateOrder = async (orderId, orderData) => {
  try {
    const endpoint = `${API_ENDPOINTS.ORDER_UPDATE}?id=${orderId}`;

    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });

    return response;
  } catch (error) {
    console.error('Update order API Error:', error);
    throw error;
  }
};

// SKU lookup API call
export const lookupSKU = async (term) => {
  try {
    const endpoint = `${API_ENDPOINTS.SKU_LOOKUP}?term=${encodeURIComponent(term)}`;

    const response = await apiRequest(endpoint, {
      method: 'GET',
    });

    return response;
  } catch (error) {
    console.error('SKU lookup API Error:', error);
    throw error;
  }
};

// SKU lookup many API call
export const lookupManySKU = async (skuData) => {
  try {
    const response = await apiRequest(API_ENDPOINTS.SKU_LOOKUP_MANY, {
      method: 'POST',
      body: JSON.stringify(skuData),
    });

    return response;
  } catch (error) {
    console.error('SKU lookup many API Error:', error);
    throw error;
  }
};

// Update order purchases API call
export const updateOrderPurchases = async (orderId, productsData) => {
  try {
    const endpoint = `${API_ENDPOINTS.UPDATE_ORDER_PURCHASES}?id=${orderId}`;

    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ products: productsData }),
    });

    return response;
  } catch (error) {
    console.error('Update order purchases API Error:', error);
    throw error;
  }
};

// Get active tags map API call
export const getActiveTagsMap = async (clientIds, skus) => {
  try {
    const response = await apiRequest(API_ENDPOINTS.GET_ACTIVE_TAGS_MAP, {
      method: 'POST',
      body: JSON.stringify({
        clientIds: clientIds,
        sku: skus
      }),
    });

    return response;
  } catch (error) {
    console.error('Get active tags map API Error:', error);
    throw error;
  }
};

// Delete order API call
export const deleteOrder = async (orderId) => {
  try {
    const endpoint = `${API_ENDPOINTS.DELETE_ORDER}?id=${orderId}`;

    const response = await apiRequest(endpoint, {
      method: 'DELETE',
    });

    return response;
  } catch (error) {
    console.error('Delete order API Error:', error);
    throw error;
  }
};

// Get all active tags API call
export const getAllActiveTags = async () => {
  try {
    const response = await apiRequest(API_ENDPOINTS.ALL_ACTIVE_TAGS, {
      method: 'GET',
    });

    return response;
  } catch (error) {
    console.error('Get all active tags API Error:', error);
    throw error;
  }
};

// Process import order API call
export const processImportOrder = async (orderId) => {
  try {
    const response = await apiRequest(API_ENDPOINTS.PROCESS_IMPORT_PRODUCT, {
      method: 'POST',
      body: JSON.stringify({
        order_id: orderId
      }),
    });

    return response;
  } catch (error) {
    console.error('Process import order API Error:', error);
    throw error;
  }
};

// Get all tag in system API call (for inventory count metadata)
export const getAllTagInSystem = async (clientId = null) => {
  try {
    let endpoint = API_ENDPOINTS.GET_ALL_TAG_IN_SYSTEM;

    // Add clientId as query parameter if provided
    if (clientId) {
      endpoint = `${endpoint}?clientId=${clientId}`;
    }

    const response = await apiRequest(endpoint, {
      method: 'GET',
    });

    return response;
  } catch (error) {
    console.error('Get all tag in system API Error:', error);
    throw error;
  }
};

// Transfer Order API calls
export const getTransferList = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add pagination params
    queryParams.append('page', params.page || 1);
    queryParams.append('limit', params.limit || 10);
    
    // Add filter params
    if (params.status) queryParams.append('status', params.status);
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.fromBranchId) queryParams.append('fromBranchId', params.fromBranchId);
    if (params.toBranchId) queryParams.append('toBranchId', params.toBranchId);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDERS}?${queryParams.toString()}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get transfer list API Error:', error);
    throw error;
  }
};

export const downloadTransferReport = async (transferId) => {
  if (!transferId) {
    throw new Error('Transfer ID is required to download the report.');
  }

  const endpoint = `${API_ENDPOINTS.TRANSFER_ORDERS}/${transferId}/export-report`;
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  });

  // Handle 401 Unauthorized - automatically logout and redirect to login
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized: Session expired. Please log in again.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to download report. ${errorText}`);
  }

  const blob = await response.blob();

  let filename = `transfer-report-${transferId}.xlsx`;
  const disposition = response.headers.get('Content-Disposition');
  if (disposition && disposition.includes('filename=')) {
    filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
  }

  return { blob, filename };
};

export const getTransferDetail = async (transferId) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_BY_ID}/${transferId}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get transfer detail API Error:', error);
    throw error;
  }
};

export const getTransferByCode = async (transferCode) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_BY_CODE}/${transferCode}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get transfer by code API Error:', error);
    throw error;
  }
};

export const createTransfer = async (transferData) => {
  try {
    // Format data according to API spec
    const payload = {
      transferCode: transferData.transferCode,
      fromBranchId: transferData.fromBranchId,
      fromBranchName: transferData.fromBranchName,
      toBranchId: transferData.toBranchId,
      toBranchName: transferData.toBranchName,
      receivedByUserId: transferData.receivedByUserId,
      receivedByUserName: transferData.receivedByUserName,
      items: transferData.items.map(item => ({
        productId: item.productId,
        productCode: item.productCode,
        productName: item.productName,
        quantity: item.quantity,
        serials: item.serials || []
      })),
      notes: transferData.notes || ''
    };
    
    const response = await apiRequest(API_ENDPOINTS.TRANSFER_ORDERS, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Create transfer API Error:', error);
    throw error;
  }
};

export const updateTransfer = async (transferId, transferData) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_BY_ID}/${transferId}`;
    
    // Format data according to API spec (only items and notes can be updated)
    const payload = {
      items: transferData.items.map(item => ({
        productId: item.productId,
        productCode: item.productCode,
        productName: item.productName,
        quantity: item.quantity,
        serials: item.serials || []
      })),
      notes: transferData.notes || ''
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return response;
  } catch (error) {
    console.error('Update transfer API Error:', error);
    throw error;
  }
};

export const confirmTransfer = async (transferId, confirmationData) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_CONFIRM}/${transferId}/confirm`;
    
    // Format confirmation data
    const payload = {
      items: confirmationData.items.map(item => ({
        productId: item.productId,
        productCode: item.productCode,
        productName: item.productName,
        confirmedQuantity: item.confirmedQuantity,
        confirmedSerials: item.confirmedSerials || [],
        missingSerials: item.missingSerials || [],
        missingReason: item.missingReason || ''
      })),
      confirmationNotes: confirmationData.confirmationNotes || '',
      missingItemsReason: confirmationData.missingItemsReason || ''
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Confirm transfer API Error:', error);
    throw error;
  }
};

export const completeTransfer = async (transferId) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_COMPLETE}/${transferId}/complete`;
    
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
    });
    
    return response;
  } catch (error) {
    console.error('Complete transfer API Error:', error);
    throw error;
  }
};

export const cancelTransfer = async (transferId) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_CANCEL}/${transferId}/cancel`;
    
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
    });
    
    return response;
  } catch (error) {
    console.error('Cancel transfer API Error:', error);
    throw error;
  }
};

export const deleteTransfer = async (transferId) => {
  try {
    const endpoint = `${API_ENDPOINTS.TRANSFER_ORDER_BY_ID}/${transferId}`;
    
    const response = await apiRequest(endpoint, {
      method: 'DELETE',
    });
    
    return response;
  } catch (error) {
    console.error('Delete transfer API Error:', error);
    throw error;
  }
};

// Alias for backward compatibility
export const processTransfer = completeTransfer;

// User Management API calls
export const getUserList = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add pagination params
    queryParams.append('page', params.page || 1);
    queryParams.append('limit', params.limit || 10);
    
    // Add filter params
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
    
    const endpoint = `${API_ENDPOINTS.USERS}?${queryParams.toString()}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get user list API Error:', error);
    throw error;
  }
};

// Get simple user list for dropdowns (id, name, username)
export const getSimpleUserList = async () => {
  try {
    const endpoint = API_ENDPOINTS.USERS_LIST;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get simple user list API Error:', error);
    throw error;
  }
};

export const getUserById = async (userId) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get user by ID API Error:', error);
    throw error;
  }
};

export const getUserByUsername = async (username) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_USERNAME}/${username}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get user by username API Error:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const payload = {
      username: userData.username,
      password: userData.password,
      roleIds: userData.roleIds || [],
      isActive: userData.isActive !== undefined ? userData.isActive : true
    };
    
    const response = await apiRequest(API_ENDPOINTS.USERS, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Create user API Error:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}`;
    
    const payload = {
      username: userData.username,
      password: userData.password,
      roleIds: userData.roleIds || [],
      isActive: userData.isActive !== undefined ? userData.isActive : true
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Update user API Error:', error);
    throw error;
  }
};

export const assignRolesToUser = async (userId, roleIds) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_ASSIGN_ROLES}/${userId}/assign-roles`;
    
    const payload = {
      roleIds: roleIds
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Assign roles to user API Error:', error);
    throw error;
  }
};

export const removeRolesFromUser = async (userId, roleIds) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_REMOVE_ROLES}/${userId}/remove-roles`;
    
    const payload = {
      roleIds: roleIds
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Remove roles from user API Error:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}`;
    
    const response = await apiRequest(endpoint, {
      method: 'DELETE',
    });
    
    return response;
  } catch (error) {
    console.error('Delete user API Error:', error);
    throw error;
  }
};

export const updateUserPermissions = async (userId, permissions) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}`;
    
    const payload = {
      permissions: permissions
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Update user permissions API Error:', error);
    throw error;
  }
};

// Update user's frontend permissions
export const updateUserFrontendPermissions = async (userId, frontendPermissions) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}/frontend-permissions`;
    
    const payload = {
      frontendPermissions: frontendPermissions
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Update user frontend permissions API Error:', error);
    throw error;
  }
};

// Add frontend permissions to user
export const addUserFrontendPermissions = async (userId, frontendPermissions) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}/frontend-permissions/add`;
    
    const payload = {
      frontendPermissions: frontendPermissions
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Add user frontend permissions API Error:', error);
    throw error;
  }
};

// Remove frontend permissions from user
export const removeUserFrontendPermissions = async (userId, frontendPermissions) => {
  try {
    const endpoint = `${API_ENDPOINTS.USER_BY_ID}/${userId}/frontend-permissions/remove`;
    
    const payload = {
      frontendPermissions: frontendPermissions
    };
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    return response;
  } catch (error) {
    console.error('Remove user frontend permissions API Error:', error);
    throw error;
  }
};

// ============================================
// RFID Scan Results API Functions
// ============================================

/**
 * Create a new RFID scan result
 * @param {Object} scanData - The scan result data
 * @returns {Promise<Object>} Created scan result with auto-calculated fields
 */
export const createRfidScanResult = async (scanData) => {
  try {
    const endpoint = API_ENDPOINTS.RFID_SCAN_RESULTS;
    
    const response = await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(scanData),
    });
    
    return response;
  } catch (error) {
    console.error('Create RFID scan result API Error:', error);
    throw error;
  }
};

/**
 * Get list of RFID scan results with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.status - Filter by status (Match/Non-Match)
 * @param {string} params.scannedBy - Filter by user who scanned
 * @param {number} params.clientId - Filter by client ID
 * @param {string} params.deviceId - Filter by device ID
 * @param {string} params.startDate - Filter by start date (ISO 8601)
 * @param {string} params.endDate - Filter by end date (ISO 8601)
 * @returns {Promise<Object>} Paginated list of scan results
 */
export const getRfidScanResults = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.scannedBy) queryParams.append('scannedBy', params.scannedBy);
    if (params.clientId) queryParams.append('clientId', params.clientId);
    if (params.deviceId) queryParams.append('deviceId', params.deviceId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULTS}?${queryParams.toString()}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get RFID scan results API Error:', error);
    throw error;
  }
};

/**
 * Get RFID scan results by client ID
 * @param {number} clientId - Client ID
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Object>} Paginated list of scan results for the client
 */
export const getRfidScanResultsByClient = async (clientId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULTS_BY_CLIENT}/${clientId}?${queryParams.toString()}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get RFID scan results by client API Error:', error);
    throw error;
  }
};

/**
 * Get RFID scan results by device ID
 * @param {string} deviceId - Device ID
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Object>} Paginated list of scan results for the device
 */
export const getRfidScanResultsByDevice = async (deviceId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULTS_BY_DEVICE}/${encodeURIComponent(deviceId)}?${queryParams.toString()}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get RFID scan results by device API Error:', error);
    throw error;
  }
};

/**
 * Get a single RFID scan result by ID
 * @param {string} id - Scan result ID
 * @returns {Promise<Object>} Scan result details
 */
export const getRfidScanResultById = async (id) => {
  try {
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULT_BY_ID}/${id}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get RFID scan result by ID API Error:', error);
    throw error;
  }
};

/**
 * Update an RFID scan result
 * @param {string} id - Scan result ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated scan result
 */
export const updateRfidScanResult = async (id, updateData) => {
  try {
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULT_BY_ID}/${id}`;
    
    const response = await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
    
    return response;
  } catch (error) {
    console.error('Update RFID scan result API Error:', error);
    throw error;
  }
};

/**
 * Delete an RFID scan result
 * @param {string} id - Scan result ID
 * @returns {Promise<void>}
 */
export const deleteRfidScanResult = async (id) => {
  try {
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULT_BY_ID}/${id}`;
    
    const response = await apiRequest(endpoint, {
      method: 'DELETE',
    });
    
    return response;
  } catch (error) {
    console.error('Delete RFID scan result API Error:', error);
    throw error;
  }
};

/**
 * Export an RFID scan result as XLSX
 * @param {string} id - Scan result ID
 * @returns {Promise<Blob>} XLSX file blob
 */
export const exportRfidScanResult = async (id) => {
  try {
    const endpoint = `${API_ENDPOINTS.RFID_SCAN_RESULTS}/${id}/export`;
    const url = endpoint.startsWith('https://') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const headers = { ...getAuthHeaders() };
    delete headers['Content-Type'];
    headers.Accept =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream';

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    // Handle 401 Unauthorized - automatically logout and redirect to login
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized: Session expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed (${response.status}): ${errorText}`);
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Export RFID scan result API Error:', error);
    throw error;
  }
};

// MQTT Device API functions
export const getUnknownDevices = async () => {
  try {
    const endpoint = `${API_ENDPOINTS.MQTT_DEVICES}?inUsed=false`;
    const response = await get(endpoint);
    return response;
  } catch (error) {
    console.error('Get unknown devices API Error:', error);
    throw error;
  }
};

export const getDeviceById = async (deviceId) => {
  try {
    const endpoint = `${API_ENDPOINTS.MQTT_DEVICE_BY_ID}/${deviceId}`;
    const response = await get(endpoint);
    return response;
  } catch (error) {
    console.error('Get device by ID API Error:', error);
    throw error;
  }
};

export const updateDeviceLocation = async (deviceId, locationName) => {
  try {
    const endpoint = `${API_ENDPOINTS.MQTT_DEVICE_LOCATION}/${deviceId}/location`;
    const response = await apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify({ locationName }),
    });
    return response;
  } catch (error) {
    console.error('Update device location API Error:', error);
    throw error;
  }
};

export const updateDevice = async (deviceId, deviceData) => {
  try {
    const endpoint = `${API_ENDPOINTS.MQTT_DEVICE_BY_ID}/${deviceId}`;
    const response = await put(endpoint, deviceData);
    return response;
  } catch (error) {
    console.error('Update device API Error:', error);
    throw error;
  }
};

// Get SKU Serials Chart data
export const getSkuSerialsChart = async (clientId) => {
  try {
    const endpoint = `${API_ENDPOINTS.SKU_SERIALS_CHART}?clientId=${clientId}`;
    
    const response = await apiRequest(endpoint, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Get SKU Serials Chart API Error:', error);
    throw error;
  }
};

export default {
  loginAPI,
  verifyTokenAPI,
  getOrderPurchases,
  getOrderDetail,
  createOrder,
  updateOrder,
  updateOrderPurchases,
  lookupSKU,
  lookupManySKU,
  getActiveTagsMap,
  getAllActiveTags,
  getAllTagInSystem,
  deleteOrder,
  processImportOrder,
  // Transfer Order functions
  getTransferList,
  getTransferDetail,
  getTransferByCode,
  createTransfer,
  updateTransfer,
  confirmTransfer,
  completeTransfer,
  cancelTransfer,
  deleteTransfer,
  downloadTransferReport,
  processTransfer,
  // User Management functions
  getUserList,
  getSimpleUserList,
  getUserById,
  getUserByUsername,
  createUser,
  updateUser,
  assignRolesToUser,
  removeRolesFromUser,
  deleteUser,
  updateUserPermissions,
  updateUserFrontendPermissions,
  addUserFrontendPermissions,
  removeUserFrontendPermissions,
  // RFID Scan Results functions
  createRfidScanResult,
  getRfidScanResults,
  getRfidScanResultsByClient,
  getRfidScanResultsByDevice,
  getRfidScanResultById,
  updateRfidScanResult,
  deleteRfidScanResult,
  // MQTT Device functions
  getUnknownDevices,
  getDeviceById,
  updateDeviceLocation,
  updateDevice,
  // Reports functions
  getSkuSerialsChart,
  // Generic HTTP methods
  get,
  post,
  put,
  delete: del,
  API_ENDPOINTS,
};
